// CGameDrawCeremony — 原版牌桌內「流局」儀式
// 對照 GameDrawAnimEntity：藍色橫帶、交錯波浪循環、流局印記由 3× 縮回，
// 動畫完成後才顯示「點擊繼續」並放行結算。
//
// === 使用圖片 ===
// - public/assets/ui/round-draw-band.png（key: ROUND_DRAW_BAND_KEY）— 原版 draw_bg.png，Upscayl 4×
// - public/assets/ui/round-draw-waves.png（key: ROUND_DRAW_WAVES_KEY）— 原版 draw.png 2-frame spritesheet，Upscayl 4×
// - public/assets/ui/round-draw-mark.png（key: ROUND_DRAW_MARK_KEY）— 原版 icon_draw.png，Upscayl 4×

import Phaser from 'phaser';

import {
  LOGICAL_HEIGHT,
  LOGICAL_WIDTH,
  SCREEN_CENTER_X,
  SCREEN_CENTER_Y,
} from '@config/game-config';
import { ROUND_DRAW_BAND_KEY, ROUND_DRAW_MARK_KEY, ROUND_DRAW_WAVES_KEY } from '@scenes/boot-scene';

import { CWinCeremonyGate } from './round-ceremony-timing';
import { UIDepth } from './ui-depths';

// ---- 全畫面層與輸入 ----
const CEREMONY_DEPTH = UIDepth.GameDrawCeremony; // 與胡牌儀式互斥，共用最高儀式層
const INPUT_DEPTH = CEREMONY_DEPTH + 2; // 全畫面點擊攔截層
const CENTER_X = SCREEN_CENTER_X; // 儀式 container 中心 X
const CENTER_Y = SCREEN_CENTER_Y; // 儀式 container 中心 Y

// ---- 原版 GameDrawAnimEntity ----
const UPSCALED_ASSET_SCALE = 0.25; // Upscayl 4× 素材縮回原始視覺尺寸
const BAND_HEIGHT = 90; // 原版 draw_bg.png 顯示高度
const WAVE_DISPLAY_WIDTH = 144; // draw.png 每格原始寬度
const WAVE_GAP = 70; // 波浪中心水平間距；保持重疊形成連續浪帶
const WAVE_COUNT = Math.ceil(LOGICAL_WIDTH / WAVE_GAP) + 2; // 多留左右各一顆避免移動時露底
const WAVE_Y = -6; // 波浪相對橫帶中心的垂直位置
const WAVE_DRIFT_X = 8; // 原版路徑最大水平位移
const WAVE_DRIFT_Y = 4; // 原版路徑最大垂直位移
const WAVE_DURATION_MS = 1500; // 原版 WAVE_DURATION
const MARK_TARGET_SCALE = UPSCALED_ASSET_SCALE; // 1080×792 → 270×198
const MARK_SCALE_FROM_RATE = 3; // 原版由 3× 縮回
const MARK_FADE_MS = 200; // 原版 FADEIN_DURATION
const MARK_SCALE_MS = 500; // 原版 SCALE_DURATION
const CONTINUE_READY_MS = 2700; // 印記 0.7s + 原版 PressAnyKey 約 2s 延遲

// ---- 「點擊繼續」----
const PRESS_BAR_Y = LOGICAL_HEIGHT * (2 / 3); // 原版 camera height × 2/3
const PRESS_BAR_HEIGHT = 48; // 黑底提示條高度
const PRESS_BAR_ALPHA = 0.75; // 黑底透明度
const PRESS_TEXT = '點擊繼續'; // 觸控版文案
const PRESS_TEXT_SIZE_PX = 28; // 黃色提示字尺寸
const PRESS_TEXT_COLOR = '#fff900'; // 原版亮黃色
const PRESS_FADE_MS = 500; // 提示淡入時長
const PRESS_SHINE_MS = 1000; // 文字明暗閃耀單程
const PRESS_TEXT_DIM_ALPHA = 0.55; // 閃耀最低透明度

/** 播放流局視覺並在動畫完成後只接受一次繼續輸入。 */
export class CGameDrawCeremony {
  private readonly gate = new CWinCeremonyGate();
  private container: Phaser.GameObjects.Container | null = null;
  private inputZone: Phaser.GameObjects.Zone | null = null;
  private readyTimer: Phaser.Time.TimerEvent | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  play(onContinue: () => void): void {
    this.destroy();
    this.gate.begin();

    const container = this.scene.add.container(CENTER_X, CENTER_Y).setDepth(CEREMONY_DEPTH);
    this.container = container;
    this.createInputGate(onContinue);
    this.createBand(container);
    this.createWaves(container);
    this.createMark(container);

    this.readyTimer = this.scene.time.delayedCall(CONTINUE_READY_MS, () => {
      this.gate.markAnimationComplete();
      this.showContinuePrompt(container);
    });
  }

  destroy(): void {
    this.readyTimer?.remove(false);
    this.readyTimer = null;

    if (this.container) {
      this.scene.tweens.killTweensOf(this.container.list);
      this.container.destroy(true);
      this.container = null;
    }
    if (this.inputZone) {
      this.inputZone.removeAllListeners();
      this.inputZone.destroy();
      this.inputZone = null;
    }
  }

  private createInputGate(onContinue: () => void): void {
    const zone = this.scene.add
      .zone(CENTER_X, CENTER_Y, LOGICAL_WIDTH, LOGICAL_HEIGHT)
      .setDepth(INPUT_DEPTH)
      .setInteractive();
    zone.on('pointerdown', () => {
      if (!this.gate.requestContinue()) return;
      this.destroy();
      onContinue();
    });
    this.inputZone = zone;
  }

  private createBand(container: Phaser.GameObjects.Container): void {
    const band = this.scene.add
      .image(0, 0, ROUND_DRAW_BAND_KEY)
      .setDisplaySize(LOGICAL_WIDTH, BAND_HEIGHT);
    container.add(band);
  }

  private createWaves(container: Phaser.GameObjects.Container): void {
    const firstX = -LOGICAL_WIDTH / 2 - WAVE_DISPLAY_WIDTH / 2;
    for (let index = 0; index < WAVE_COUNT; index++) {
      const wave = this.scene.add
        .image(firstX + index * WAVE_GAP, WAVE_Y, ROUND_DRAW_WAVES_KEY, index % 2)
        .setScale(UPSCALED_ASSET_SCALE)
        .setBlendMode(Phaser.BlendModes.ADD);
      container.add(wave);
      this.scene.tweens.add({
        targets: wave,
        x: wave.x + (index % 2 === 0 ? WAVE_DRIFT_X : -WAVE_DRIFT_X),
        y: wave.y + (index % 2 === 0 ? WAVE_DRIFT_Y : -WAVE_DRIFT_Y),
        duration: WAVE_DURATION_MS,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }

  private createMark(container: Phaser.GameObjects.Container): void {
    const mark = this.scene.add
      .image(0, 0, ROUND_DRAW_MARK_KEY)
      .setScale(MARK_TARGET_SCALE * MARK_SCALE_FROM_RATE)
      .setAlpha(0);
    container.add(mark);

    this.scene.tweens.add({
      targets: mark,
      alpha: 1,
      duration: MARK_FADE_MS,
      ease: 'Quad.easeOut',
    });
    this.scene.tweens.add({
      targets: mark,
      scaleX: MARK_TARGET_SCALE,
      scaleY: MARK_TARGET_SCALE,
      duration: MARK_SCALE_MS,
      ease: 'Cubic.easeIn',
    });
  }

  private showContinuePrompt(container: Phaser.GameObjects.Container): void {
    const localY = PRESS_BAR_Y - CENTER_Y;
    const bar = this.scene.add
      .rectangle(0, localY, LOGICAL_WIDTH, PRESS_BAR_HEIGHT, 0x000000, PRESS_BAR_ALPHA)
      .setAlpha(0);
    const text = this.scene.add
      .text(0, localY, PRESS_TEXT, {
        fontFamily: 'sans-serif',
        fontSize: `${PRESS_TEXT_SIZE_PX}px`,
        color: PRESS_TEXT_COLOR,
      })
      .setOrigin(0.5)
      .setAlpha(0);
    container.add([bar, text]);
    this.scene.tweens.add({
      targets: [bar, text],
      alpha: 1,
      duration: PRESS_FADE_MS,
    });
    this.scene.tweens.add({
      targets: text,
      alpha: PRESS_TEXT_DIM_ALPHA,
      duration: PRESS_SHINE_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
}
